/**
 * Backend adapter — bridge giữa skeleton 3D shape (Customer/Drink/ServeResult)
 * và REST API backend thật.
 *
 * Skeleton dùng:
 *   - CustomerType: 'student' | 'office' | 'shipper' (note: 'office' not 'office_worker')
 *   - DrinkId: 'ca_phe_den_da' | 'ca_phe_sua_da' | 'bac_xiu' | …
 *   - ServeResult: { status, reward, newBalance }
 *
 * Backend (B5/B6) dùng:
 *   - customer_type: 'student' | 'office_worker' | 'shipper'
 *   - requested_recipe: same code
 *   - response shape: shop_state + revenue + next_order + milestones
 *
 * Adapter này chỉ chuyển shape, KHÔNG thêm business logic.
 */
import { api, extractErrorMessage } from "./api";
import { newActionId } from "./idempotency";
import type {
  Customer,
  CustomerType as SkeletonCustomerType,
  DrinkId,
  IngredientId,
  ServeResult,
} from "../types/game-3d";

// ─── Type mapping ───
type BackendCustomerType = "student" | "office_worker" | "shipper";

function mapCustomerType(be: BackendCustomerType): SkeletonCustomerType {
  return be === "office_worker" ? "office" : (be as SkeletonCustomerType);
}

function unmapCustomerType(skel: SkeletonCustomerType): BackendCustomerType {
  return skel === "office" ? "office_worker" : (skel as BackendCustomerType);
}

const CUSTOMER_NAME_VI: Record<SkeletonCustomerType, string> = {
  student: "Sinh viên",
  office: "Nhân viên VP",
  shipper: "Shipper",
  neighbor: "Cô chú",
  tiktoker: "TikToker",
};

const PATIENCE_SECONDS: Record<SkeletonCustomerType, number> = {
  student: 75,
  office: 60,
  shipper: 45,
  neighbor: 100,
  tiktoker: 80,
};

// ─── Backend API shapes (subset) ───
interface BackendOrder {
  order_id: string;
  customer_type: BackendCustomerType;
  customer_dialogue: string;
  requested_recipe: DrinkId;
  requested_recipe_display: string;
  spawned_at: string;
  patience_deadline: string;
  is_rush_hour: boolean;
}

interface BackendShopState {
  id: string;
  name: string | null;
  bean_coin: number;
  reputation: number;
  energy: number;
  energy_max: number;
  level: number;
  upgrades: Record<string, number>;
  current_streak?: number;
}

interface BackendRevenue {
  session_revenue: number;
  today_revenue: number;
  shop_balance: number;
  today_revenue_delta_pct: number | null;
  today_orders: number;
}

interface BackendSession {
  session_id: string;
  shop_id: string;
  status: "active" | "ended";
  started_at: string;
  ended_at: string | null;
  total_orders_served: number;
  total_bean_earned: number;
}

interface BackendServeResponse {
  order_id: string;
  is_correct: boolean;
  timed_out: boolean;
  bean_earned: number;
  tip_earned: number;
  reputation_earned: number;
  is_rush_hour: boolean;
  shop_state: BackendShopState;
  revenue: BackendRevenue;
  next_order: BackendOrder | null;
  milestones_unlocked: Array<{ code: string; rewards: Record<string, number> }>;
  hint: string | null;
}

interface BackendActiveSession {
  session: BackendSession | null;
  current_order: BackendOrder | null;
  shop_state: BackendShopState;
  revenue: BackendRevenue;
}

interface BackendStartSession {
  session: BackendSession;
  current_order: BackendOrder;
  shop_state: BackendShopState;
  revenue: BackendRevenue;
}

// ─── Public types tổng hợp dùng cho cafe3dStore ───
export interface ShopSnapshot {
  beanCoin: number;
  reputation: number;
  energy: number;
  energyMax: number;
  level: number;
  shopName: string | null;
  upgrades: Record<string, number>;
  revenueToday: number;
  isRushHour: boolean;
  currentStreak: number;
}

export interface SessionContext {
  sessionId: string | null;
  customer: Customer | null;
  orderId: string | null;
  orderSpawnedAt: number; // ms epoch
  recipeIngredients: IngredientId[]; // expected để show hint khi sai
}

export interface UnlockedMilestone {
  code: string;
  rewardBean: number;
}

export interface ExtendedServeResult extends ServeResult {
  milestones: UnlockedMilestone[];
  nextSession: SessionContext;
}

// ─── Converters ───
function toCustomer(order: BackendOrder): Customer {
  const skelType = mapCustomerType(order.customer_type);
  return {
    id: order.order_id,
    type: skelType,
    name_vi: CUSTOMER_NAME_VI[skelType],
    patience_seconds: PATIENCE_SECONDS[skelType],
    dialogue_vi: order.customer_dialogue,
    dialogue_en: order.customer_dialogue, // Phase 1 chỉ vi
    drink: order.requested_recipe,
  };
}

function toShopSnapshot(
  shop: BackendShopState,
  rev: BackendRevenue,
  isRush: boolean,
): ShopSnapshot {
  return {
    beanCoin: shop.bean_coin,
    reputation: shop.reputation,
    energy: shop.energy,
    energyMax: shop.energy_max,
    level: shop.level,
    shopName: shop.name,
    upgrades: shop.upgrades,
    revenueToday: rev.today_revenue,
    isRushHour: isRush,
    currentStreak: shop.current_streak ?? 0,
  };
}

/** Local rush-hour heuristic (backend Phase 1 luôn trả is_rush_hour=false). */
function localIsRushHour(): boolean {
  const now = new Date();
  const time = now.getHours() + now.getMinutes() / 60;
  return (
    (time >= 7 && time < 9) ||
    (time >= 11.5 && time < 13) ||
    (time >= 19 && time < 21)
  );
}

// ─── Public API ───

/** Load shop + revenue + active session (resume nếu có). */
export async function loadInitialState(): Promise<{
  shop: ShopSnapshot;
  session: SessionContext;
}> {
  const res = await api.get<{ data: BackendActiveSession }>(
    "/sessions/active",
  );
  const d = res.data.data;
  const isRush = localIsRushHour();
  const shop = toShopSnapshot(d.shop_state, d.revenue, isRush);

  let session: SessionContext;
  if (d.session && d.current_order) {
    session = {
      sessionId: d.session.session_id,
      customer: toCustomer(d.current_order),
      orderId: d.current_order.order_id,
      orderSpawnedAt: new Date(d.current_order.spawned_at).getTime(),
      recipeIngredients: [], // sẽ fetch lazy nếu cần
    };
  } else {
    session = {
      sessionId: null,
      customer: null,
      orderId: null,
      orderSpawnedAt: 0,
      recipeIngredients: [],
    };
  }
  return { shop, session };
}

/** Start session mới → trả về customer đầu tiên. */
export async function startSession(): Promise<{
  shop: ShopSnapshot;
  session: SessionContext;
}> {
  const res = await api.post<{ data: BackendStartSession }>(
    "/sessions/start",
    { client_action_id: newActionId() },
  );
  const d = res.data.data;
  const isRush = localIsRushHour();
  return {
    shop: toShopSnapshot(d.shop_state, d.revenue, isRush),
    session: {
      sessionId: d.session.session_id,
      customer: toCustomer(d.current_order),
      orderId: d.current_order.order_id,
      orderSpawnedAt: new Date(d.current_order.spawned_at).getTime(),
      recipeIngredients: [],
    },
  };
}

/** Serve order + auto-spawn next order. */
export async function serveOrderApi(
  sessionId: string,
  orderId: string,
  ingredients: IngredientId[],
): Promise<{
  result: ExtendedServeResult;
  shop: ShopSnapshot;
}> {
  const res = await api.post<{ data: BackendServeResponse }>(
    `/sessions/${sessionId}/orders/${orderId}/serve`,
    { client_action_id: newActionId(), ingredients },
  );
  const d = res.data.data;
  const isRush = localIsRushHour();
  const shop = toShopSnapshot(d.shop_state, d.revenue, isRush);

  let status: ServeResult["status"];
  if (d.timed_out) status = "timeout";
  else if (d.is_correct) status = "correct";
  else status = "wrong";

  const nextSession: SessionContext = d.next_order
    ? {
        sessionId,
        customer: toCustomer(d.next_order),
        orderId: d.next_order.order_id,
        orderSpawnedAt: new Date(d.next_order.spawned_at).getTime(),
        recipeIngredients: [],
      }
    : {
        sessionId,
        customer: null,
        orderId: null,
        orderSpawnedAt: 0,
        recipeIngredients: [],
      };

  return {
    result: {
      status,
      reward: {
        coin: d.bean_earned,
        reputation: d.reputation_earned,
        tip: d.tip_earned,
      },
      newBalance: {
        beanCoin: d.shop_state.bean_coin,
        reputation: d.shop_state.reputation,
        revenueToday: d.revenue.today_revenue,
      },
      milestones: d.milestones_unlocked.map((m) => ({
        code: m.code,
        rewardBean: m.rewards.bean ?? 0,
      })),
      nextSession,
    },
    shop,
  };
}

/** End session — gọi khi user click "Kết thúc ca" hoặc energy hết. */
export async function endSessionApi(
  sessionId: string,
): Promise<{
  summary: {
    totalOrders: number;
    totalCorrect: number;
    totalWrong: number;
    totalAbandoned: number;
    totalBeanEarned: number;
    durationSeconds: number;
  };
}> {
  const res = await api.post<{
    data: {
      summary: {
        total_orders: number;
        total_correct: number;
        total_wrong: number;
        total_abandoned: number;
        total_bean_earned: number;
        duration_seconds: number;
      };
    };
  }>(`/sessions/${sessionId}/end`, { client_action_id: newActionId() });
  const s = res.data.data.summary;
  return {
    summary: {
      totalOrders: s.total_orders,
      totalCorrect: s.total_correct,
      totalWrong: s.total_wrong,
      totalAbandoned: s.total_abandoned,
      totalBeanEarned: s.total_bean_earned,
      durationSeconds: s.duration_seconds,
    },
  };
}

export {
  mapCustomerType,
  unmapCustomerType,
  CUSTOMER_NAME_VI,
  localIsRushHour,
  extractErrorMessage,
};
